import { ContentCopy, Download } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import {
	GridActionsCellItem,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
	GridRowParams,
} from '@mui/x-data-grid';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePaginatedDisbursements } from '../../../api/DisbursementsApi';
import { usePolicy } from '../../../api/PolicyApi';
import { useAuthContext } from '../../../context/auth';
import { CurrencyCode } from '../../../types/currencyCode';
import { Disbursement } from '../../../types/disbursements';
import { formatCurrency } from '../../../util/util';
import { LinkCell } from '../../grid';
import GridWithToolbar from '../../grid/GridWithToolbar';
import { infoToast } from '../../Toast';
import DisbursementStateChip from '../components/DisbursementStateChip';
import {
	COPY_DISBURSEMENT_LOCATOR,
	COPY_DISBURSEMENT_LOCATOR__SUCCESS,
	DISBURSEMENTS_PAGE_TITLE,
	DISBURSEMENTS_PAGE__ADD_DISBURSEMENT_BUTTON,
	DISBURSEMENTS_PAGE__EDIT_BUTTON,
	DISBURSEMENTS_PAGE__TABLE_HEADER__AMOUNT,
	DISBURSEMENTS_PAGE__TABLE_HEADER__CREATED_DATE,
	DISBURSEMENTS_PAGE__TABLE_HEADER__DISPLAY_ID,
	DISBURSEMENTS_PAGE__TABLE_HEADER__STATUS,
	DOWNLOAD_DISBURSEMENT_DOCUMENT,
} from '../data/constants';

const DisbursementsPage = () => {
	const authData = useAuthContext();
	const params = useParams();
	const policyLocator = params.policyId;
	const policyholderLocator = params.policyholderId;
	const navigate = useNavigate();
	const authToken = authData.auth?.authorizationToken;
	const [page, setPage] = React.useState({
		page: 0,
		pageSize: 25,
	});

	const { data: policyResponse } = usePolicy(
		policyLocator,
		authData?.auth?.authorizationToken,
	);

	const {
		data: disbursementsResponse,
		isLoading,
		fetchNextPage,
		fetchPreviousPage,
		isFetchingNextPage,
		hasNextPage,
	} = usePaginatedDisbursements(
		page.pageSize,
		authToken,
		policyholderLocator,
		policyLocator,
	);

	const currPage = disbursementsResponse?.pages[page.page];
	const disbursements = currPage?.data.disbursements || [];

	const StateChip = (params: GridRenderCellParams<Disbursement>) => {
		return <DisbursementStateChip state={params.row.state} />;
	};

	const getRowActions = (params: GridRowParams<Disbursement>) => {
		const rowActions = [];
		if (
			params.row.state === 'draft' &&
			authData.hasPermission('disbursement.update')
		) {
			rowActions.push(
				<GridActionsCellItem
					icon={<EditIcon />}
					label={DISBURSEMENTS_PAGE__EDIT_BUTTON}
					onClick={() => navigate(`${params.row.locator}/edit`)}
				/>,
			);
		}
		if (params.row.state === 'issued') {
			if (params.row?.documents?.length) {
				rowActions.push(
					<GridActionsCellItem
						icon={<Download />}
						label={DOWNLOAD_DISBURSEMENT_DOCUMENT}
						onClick={() => window.open(params.row?.documents[0].url, '_blank')}
						showInMenu
					/>,
				);
			}
			rowActions.push(
				<GridActionsCellItem
					icon={<ContentCopy />}
					onClick={() => {
						navigator.clipboard.writeText(params.row.locator);
						infoToast(COPY_DISBURSEMENT_LOCATOR__SUCCESS);
					}}
					label={COPY_DISBURSEMENT_LOCATOR}
					showInMenu
				/>,
			);
		}
		return rowActions;
	};

	const columns: GridColDef[] = [
		{
			field: 'displayId',
			headerName: DISBURSEMENTS_PAGE__TABLE_HEADER__DISPLAY_ID,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => (
				<LinkCell label={params.value} to={params.row.locator} />
			),
		},
		{
			field: 'amount',
			headerName: DISBURSEMENTS_PAGE__TABLE_HEADER__AMOUNT,
			flex: 0.2,
			sortable: false,
			renderCell: (params) => params.row.amountFormatted,
		},
		{
			field: 'state',
			headerName: DISBURSEMENTS_PAGE__TABLE_HEADER__STATUS,
			flex: 0.2,
			sortable: false,
			renderCell: StateChip,
		},
		{
			field: 'createdTimestamp',
			headerName: DISBURSEMENTS_PAGE__TABLE_HEADER__CREATED_DATE,
			sortable: false,
			flex: 0.2,
		},
		{
			field: 'actions',
			type: 'actions',
			align: 'right',
			flex: 0.2,
			sortable: false,
			getActions: getRowActions,
		},
	];

	const handlePageChange = (model: GridPaginationModel) => {
		if (model.pageSize !== page.pageSize) {
			setPage({
				page: 0,
				pageSize: model.pageSize,
			});
		} else {
			setPage({
				page: model.page,
				pageSize: model.pageSize,
			});
			if (model.page > page.page) {
				fetchNextPage();
			}
			if (model.page < page.page) {
				fetchPreviousPage();
			}
		}
	};

	const formattedData = disbursements.map((disbursement) => {
		return {
			...disbursement,
			amount: disbursement.amount,
			amountFormatted: formatCurrency(
				parseFloat(disbursement.amount),
				disbursement.amountCurrency as CurrencyCode,
			),
			createdTimestamp: disbursement.createdTimestamp
				? authData.getZonedFormattedDateString(disbursement.createdTimestamp)
				: '',
		};
	});

	const hasPositiveCashBalance =
		policyResponse?.cashBalance?.amount &&
		parseFloat(policyResponse?.cashBalance?.amount) > 0;
	const canCreateDisbursement =
		policyLocator &&
		hasPositiveCashBalance &&
		authData.hasPermission('disbursement.create');

	return (
		<>
			<GridWithToolbar
				rowId='locator'
				title={DISBURSEMENTS_PAGE_TITLE}
				key={disbursements?.length}
				data={formattedData || []}
				columns={columns}
				loading={isLoading || isFetchingNextPage}
				buttonColor={'primary'}
				buttonText={DISBURSEMENTS_PAGE__ADD_DISBURSEMENT_BUTTON}
				onButtonClick={canCreateDisbursement ? () => navigate('new') : null} // sending null will not display button
				paginationMode='server'
				rowCount={1000}
				paginationModel={page}
				onPaginationModelChange={handlePageChange}
				hasNextPage={
					hasNextPage || disbursementsResponse?.pages?.length > page.page + 1
				}
			/>
		</>
	);
};

export default DisbursementsPage;
